import { useNavigate } from 'react-router-dom';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { DuplicateLeadInfo, DuplicateStudentInfo } from '../../types/lead';
import { useAuth } from '../../context/authContext';

interface DuplicatePersonDialogProps {
  open: boolean;
  leads: DuplicateLeadInfo[];
  students: DuplicateStudentInfo[];
  onClose: () => void;
}

const row = (label: string, value: string) => (
  <Stack direction="row" justifyContent="space-between" key={label}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Stack>
);

/** Duplicate-person check's popup (Quick Lead correction, sections 6-9):
 * searches both Leads and registered Students, and a match of either
 * kind blocks creation outright — this dialog never offers a "Create
 * Anyway" option, only ways to go look at (or, for Lead Manager,
 * transfer) the existing record(s). Handles every combination: a single
 * Lead, a single Student, both, or multiple Leads. */
export const DuplicatePersonDialog = ({ open, leads, students, onClose }: DuplicatePersonDialogProps) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  if (leads.length === 0 && students.length === 0) return null;

  const title =
    students.length > 0 && leads.length > 0
      ? 'Existing Records Found'
      : students.length > 0
        ? 'Existing Student Found'
        : leads.length > 1
          ? `Existing Leads Found: ${leads.length}`
          : 'Existing Lead Found';

  const summary =
    students.length > 0
      ? 'This person is already registered as a student. No new lead can be created for this phone number.'
      : leads.length > 1
        ? 'Multiple existing leads already use this phone number. No new lead can be created for it.'
        : 'A lead with this phone number already exists. A new lead was not created.';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {summary}
        </Alert>

        {leads.length > 0 && (
          <Stack spacing={1.5} sx={{ mb: students.length > 0 ? 2 : 0 }}>
            {leads.length > 1 && (
              <Typography variant="subtitle2" color="text.secondary">
                LEAD{leads.length > 1 ? 'S' : ''}
              </Typography>
            )}
            {leads.map((lead, i) => (
              <Stack key={lead.id} spacing={1}>
                {i > 0 && <Divider />}
                {row('Lead ID', lead.leadNumber)}
                {row('Name', `${lead.firstName} ${lead.lastName}`)}
                {row('Phone', lead.phone)}
                {row('Branch', lead.branch?.name ?? '—')}
                {row(
                  'Assigned Counselor',
                  lead.assignedTo ? `${lead.assignedTo.firstName} ${lead.assignedTo.lastName}` : 'Unassigned',
                )}
                {row('Lead Status', lead.leadStatus.name)}
                {row('Approval', lead.approvalStatus.replace('_', ' '))}
                {row('Created', new Date(lead.createdAt).toLocaleDateString())}
                {lead.lastFollowUpAt != null && row('Last Follow-up', new Date(lead.lastFollowUpAt).toLocaleDateString())}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  {hasPermission('leads', 'transfer') && (
                    <Button size="small" onClick={() => navigate(`/leads/${lead.id}`, { state: { openAssign: true } })}>
                      Transfer
                    </Button>
                  )}
                  <Button size="small" variant="outlined" onClick={() => navigate(`/leads/${lead.id}`)}>
                    View Lead
                  </Button>
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}

        {students.length > 0 && (
          <Stack spacing={1.5}>
            {leads.length > 0 && (
              <Typography variant="subtitle2" color="text.secondary">
                STUDENT
              </Typography>
            )}
            {students.map((student) => (
              <Stack key={student.id} spacing={1}>
                {row('Student ID', student.studentNumber)}
                {row('Name', `${student.firstName} ${student.lastName}`)}
                {row('Phone', student.phone)}
                {row('Branch', student.branch?.name ?? '—')}
                {row('Student Status', student.isDraft ? 'Draft' : 'Registered')}
                {row('Created', new Date(student.createdAt).toLocaleDateString())}
                <Stack direction="row" justifyContent="flex-end">
                  <Button size="small" variant="outlined" onClick={() => navigate(`/students/${student.id}`)}>
                    View Student
                  </Button>
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
